import { formatRowForRendering } from '@/utils/results';

function formatResultViewForBackend(resultView) {
  const {
    name = '',
    entriesUuids = [],
    site = [],
    stage = [],
    environment = 'live',
    status = 'included',
    cohort = '',
    gender = [],
    birthYear = [],
    subjects = [],
  } = resultView;

  let yearOfBirthType = 'single';
  yearOfBirthType = birthYear?.length === 2 ? 'range' : birthYear?.length > 2 ? 'multiple' : yearOfBirthType;

  return {
    name,
    entriesUuids,
    filters: {
      site: site.map((siteUUID) => ({ uuid: siteUUID })),
      stage: stage.map((stageUUId) => ({ uuid: stageUUId })),
      subject: {
        id: subjects.map((uuid) => ({ uuid })),
        environment,
        status,
        cohort: cohort
          .split(',')
          .map((cohort) => cohort.trim())
          .filter((cohort) => cohort),
        ...(!gender?.length ? null : { gender }),
        ...(!birthYear ? null : { birthYear: { years: birthYear, type: yearOfBirthType } }),
      },
    },
  };
}

export const ProjectResultsViews = {
  namespaced: true,

  state: {
    form: {
      name: '',
      stage: [],
      birthYear: [],
      gender: [],
      cohort: '',
      site: [],
      entriesUuids: [],
      subjects: [],
    },
    isLoading: false,
    views: [],
    currentView: {},
    createdView: null,
    resultViewTableData: {},
    isLoadingResultsViewTableData: false,
    resultsViewData: { totalItems: 0, perPage: 50, page: 1 },
    requestViewError: null,
    requestResultViewTableDataError: null,
    exportResultsViewUrl: null,
    exportingResultsViews: false,
    showExportBtnInResultsViewTable: false,
    sources: {},
    isGettingSource: false,
    canCreateViews: false,
    currentViewPermissions: {},
    isDeletingView: false,
    listViewsPerPage: 10,
  },

  getters: {
    form: ({ form }) => form,
    isLoading: ({ isLoading }) => isLoading,
    isLoadingResultsViewTableData: ({ isLoadingResultsViewTableData }) => isLoadingResultsViewTableData,
    views: ({ views }) => views,
    currentView: ({ currentView }) => currentView,
    createdView: ({ createdView }) => createdView,
    fields: ({ resultViewTableData }) => resultViewTableData?.data?.columns || [],
    rows: ({ resultViewTableData }) => resultViewTableData?.data?.rows || [],
    pagination: ({ resultViewTableData }) => resultViewTableData?.meta || {},
    resultsViewData: ({ resultsViewData }) => resultsViewData,
    requestViewError: ({ requestViewError }) => requestViewError,
    requestResultViewTableDataError: ({ requestResultViewTableDataError }) => requestResultViewTableDataError,
    exportResultsViewUrl: ({ exportResultsViewUrl }) => exportResultsViewUrl,
    exportingResultsViews: ({ exportingResultsViews }) => exportingResultsViews,
    showExportBtnInResultsViewTable: ({ showExportBtnInResultsViewTable }) => showExportBtnInResultsViewTable,
    getSource:
      ({ sources }) =>
      (source) =>
        sources[source] || null,
    isGettingSource: ({ isGettingSource }) => isGettingSource,
  },

  mutations: {
    setForm(state, form) {
      Vue.set(state, 'form', form);
    },

    setIsSaving(state, isSaving) {
      state.isSaving = isSaving;
    },

    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },

    setIsLoadingResultsViewTableData(state, isLoading) {
      state.isLoadingResultsViewTableData = isLoading;
    },

    setViews(state, views, append = true) {
      if (!append) {
        Vue.set(state, 'views', views);
        return;
      }
      const newViews = [...state.views, ...views];
      Vue.set(state, 'views', newViews);
    },

    setCurrentView(state, view) {
      Vue.set(state, 'currentViewPermissions', {});
      if (Object.keys(view).length === 0) {
        Vue.set(state, 'currentView', {});
        return;
      }

      const {
        uuid,
        name,
        entriesUuids,
        filters: {
          site,
          stage,
          subject: {
            id = [],
            cohort,
            gender,
            birthYear: { years },
          },
        },
        can,
      } = view;
      Vue.set(state, 'currentView', {
        name,
        entriesUuids,
        site: site.map((site) => site.uuid),
        stage: stage.map((stage) => stage.uuid),
        birthYear: years,
        cohort: (cohort || []).join(', '),
        gender,
        uuid,
        subjects: id.map((subject) => subject.uuid),
      });

      Vue.set(state, 'currentViewPermissions', can);
    },

    setCreatedView(state, viewUUID) {
      state.createdView = viewUUID;
    },

    setResultViewTableData(state, data) {
      Vue.set(state, 'resultViewTableData', data);
    },

    setResultViewData(state, { per_page: perPage, total, current_page: page }) {
      Vue.set(state, 'resultsViewData', {
        totalItems: total,
        perPage,
        page,
      });
    },

    setRequestViewError(state, error) {
      state.requestViewError = error;
    },

    setRequestResultViewTableDataError(state, error) {
      state.requestResultViewTableDataError = error;
    },

    setExportResultsViewUrl(state, url) {
      state.exportResultsViewUrl = url;
    },

    setExportingResultsViews(state, exporting) {
      state.exportingResultsViews = exporting;
    },

    setShowExportBtnInResultsViewTable(state, show) {
      state.showExportBtnInResultsViewTable = show;
    },

    setSource(state, { entryDataId, data }) {
      const sources = state.sources;
      sources[entryDataId] = data;
      Vue.set(state, 'sources', sources);
    },

    setIsGettingSource(state, isGetting) {
      state.isGettingSource = isGetting;
    },

    setCanCreateViews(state, canCreate) {
      state.canCreateViews = canCreate;
    },

    setIsDeletingView(state, isDeleting) {
      state.isDeletingView = isDeleting;
    },

    resetCurrentView(state) {
      const {
        currentView: { uuid },
      } = state;
      Vue.set(state, 'currentViewPermissions', {});
      Vue.set(state, 'currentView', {});
      Vue.set(
        state,
        'views',
        state.views.filter((view) => view.uuid !== uuid)
      );
    },

    resetResultsViews(state) {
      Vue.set(state, 'views', []);
      Vue.set(state, 'resultsViewData', { totalItems: 0, perPage: 50, page: 1 });
      Vue.set(state, 'createdView', null);
      Vue.set(state, 'currentViewPermissions', {});
    },
  },

  actions: {
    async getViews({ commit }, { repository, project, perPage, page }) {
      if (!project || !repository) {
        return;
      }

      commit('setIsLoading', true);
      commit('setCanCreateViews', false);
      try {
        const response = await this._vm.$api('repositories.projects.result.views.list', {
          version: '1.0',
          project,
          repository,
          perPage,
          page,
        });

        commit('setViews', response.data.data);
        commit('setResultViewData', response.data.meta);
        commit('setCanCreateViews', response.data.can?.create);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setIsLoading', false);
      }
    },

    async getView({ commit }, { repository, project, view }) {
      if (!project || !repository || !view) {
        return;
      }
      commit('setRequestViewError', null);

      commit('setIsLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.result.views.find', {
          version: '1.0',
          project,
          repository,
          result_views: view,
        });

        commit('setCurrentView', response.data.data);
      } catch (err) {
        console.error(err);
        commit('setRequestViewError', err);
      } finally {
        commit('setIsLoading', false);
      }
    },

    async createView({ commit }, { repository, project, form }) {
      if (!project || !repository || !form) {
        return;
      }

      commit('setIsLoading', true);
      try {
        const response = await this._vm.$api(
          'repositories.projects.result.views.store',
          {
            project,
            repository,
          },
          formatResultViewForBackend(form)
        );
        commit('setCreatedView', response.data.data.uuid);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setIsLoading', false);
      }
    },

    async updateView({ commit }, { repository, project, view, form }) {
      if (!project || !repository || !view) {
        return;
      }

      commit('setIsLoading', true);
      try {
        await this._vm.$api(
          'repositories.projects.result.views.update',
          {
            version: '1.0',
            project,
            repository,
            result_views: view,
          },
          formatResultViewForBackend(form)
        );
      } catch (err) {
        console.error(err);
      } finally {
        commit('setIsLoading', false);
      }
    },

    async deleteView({ commit }, { repository, project, view }) {
      if (!project || !repository || !view) {
        return;
      }

      commit('setIsLoading', true);
      commit('setIsDeletingView', true);
      try {
        await this._vm.$api('repositories.projects.result.views.delete', {
          version: '1.0',
          project,
          repository,
          result_view: view,
        });
      } catch (err) {
        console.error(err);
      } finally {
        commit('setIsLoading', false);
        commit('setIsDeletingView', false);
        commit('resetCurrentView');
      }
    },

    async loadResultsViewTableData({ commit }, { repository, project, resultView, page, perPage, sortBy, sortDesc }) {
      if (!project || !repository || !resultView) {
        return;
      }

      commit('setIsLoadingResultsViewTableData', true);
      try {
        const { data } = await this._vm.$api('repositories.projects.result.views.table', {
          version: '1.0',
          project,
          repository,
          result_views: resultView,
          page,
          perPage,
          sortBy,
          sortDesc,
        });

        const { rows, columns } = data.data;

        data.data.rows = formatRowForRendering(rows, columns);
        commit('setResultViewTableData', data);
        commit('setShowExportBtnInResultsViewTable', data.meta.total > 0);
      } catch (err) {
        console.error(err);
        commit('setRequestResultViewTableDataError', err);
      } finally {
        commit('setIsLoadingResultsViewTableData', false);
      }
    },

    resetResultsViewTableData({ commit }) {
      commit('setResultViewTableData', {});
      commit('setShowExportBtnInResultsViewTable', false);
    },

    async exportResultsViewTableData({ commit }, { repository, project, resultView }) {
      if (!project || !repository || !resultView) {
        return;
      }

      commit('setExportingResultsViews', true);

      try {
        const response = await this._vm.$api('repositories.projects.result.views.table.export', {
          version: '1.0',
          project,
          repository,
          result_views: resultView,
        });
        commit('setExportResultsViewUrl', response.data.data);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setExportingResultsViews', false);
      }
    },

    async loadSource({ commit }, { repository, project, entryDataId }) {
      commit('setSource', { entryDataId, data: null });
      commit('setIsGettingSource', true);
      if (!project || !repository || !entryDataId) {
        return;
      }

      try {
        const response = await this._vm.$api('repositories.projects.results.entryData', {
          version: '1.0',
          project,
          repository,
          entryData: entryDataId,
        });
        commit('setSource', { entryDataId, data: response.data.data });
      } catch (err) {
        console.error(err);
      } finally {
        commit('setIsGettingSource', false);
      }
    },
  },
};
